<template>
	<base-modal
		ref="$modal"
		:title="modalTitle"
		:loading="isLoading || isSaving"
		width="640px"
		height="80vh"
		@on-close="deactivateEsc"
	>
		<div
			ref="$content"
			class="bill-info"
		>
			<template v-if="!isLoading && info">
				<div class="bill-info__line">
					<span class="bill-info__label">{{ $t('status_label') }}:</span>
					<span class="bill-info__value">{{ statusText }}</span>
				</div>

				<div class="bill-info__line">
					<span class="bill-info__label">{{ $t('creation_date') }}:</span>
					<span class="bill-info__value">
						{{ formatShortDate(info.Date) }}<template v-if="formatShortDate(info.Date)">,</template>
						{{ $t(info.CreatedInCrm ? 'created_in_crm' : 'created_in_one_c') }}<template v-if="info.Creator?.Name">, {{ info.Creator.Name }}</template>
					</span>
				</div>

				<div class="bill-info__line">
					<span class="bill-info__label">{{ $t('type_label') }}:</span>
					<span class="bill-info__value">{{ typeText }}</span>
				</div>

				<div class="bill-info__line">
					<span class="bill-info__label">{{ $t('counterparty') }}:</span>
					<span
						v-if="info.Counterparty?.Id"
						class="bill-info__cp"
						@click="openCounterparty"
					>{{ info.Counterparty.Name || '—' }}</span>
					<span
						v-else
						class="bill-info__value"
					>{{ info.Counterparty?.Name || '—' }}</span>
				</div>

				<!-- Номер -->
				<div class="bill-info__line bill-info__line--field">
					<span class="bill-info__label">{{ $t('number') }}:</span>
					<span
						v-if="!isEditing || isOutgoing"
						class="bill-info__value"
					>{{ info.Number || '—' }}</span>
					<app-input
						v-else
						v-model="editForm.Number"
						class="bill-info__input"
						type="number"
						:placeholder="$t('number')"
					/>
				</div>

				<!-- Сумма -->
				<div class="bill-info__line bill-info__line--field">
					<span class="bill-info__label">{{ $t('amount') }}:</span>
					<span
						v-if="!isEditing"
						class="bill-info__value"
					>
						{{ formatMoney(info.CalculatedAmount) }}
						<span class="bill-info__bar">
							<span
								class="bill-info__bar-fill"
								:style="{ width: barWidth(info.PayedPercent) }"
							/>
						</span>
						<span
							v-if="info.HasPayments"
							class="bill-info__green"
						>{{ info.PayedPercent ?? 0 }}% ({{ formatMoney(info.PayedAmount) }})</span>
						<span v-else>{{ payStatusText(info.PayStatus) }}</span>
					</span>
					<span
						v-else
						class="bill-info__field"
					>
						<app-input
							v-model="editForm.Sum"
							class="bill-info__input"
							type="number"
							:placeholder="$t('amount')"
							:error="sumInvalid"
						/>
						<span
							v-if="sumInvalid"
							class="bill-info__error"
						>{{ $t('bill_amount_placeholder') }}</span>
					</span>
				</div>

				<div
					v-if="info.HasPayments && !isEditing"
					class="bill-info__line"
				>
					<span class="bill-info__label">{{ $t('paid_label') }}:</span>
					<span class="bill-info__value">{{ formatMoney(info.PayedAmount) }}</span>
				</div>

				<!-- Платежи -->
				<div
					v-if="info.Payments?.length && !isEditing"
					class="bill-info__block"
				>
					<div class="bill-info__label">{{ $t('payments') }}:</div>
					<div
						v-for="(p, i) in info.Payments"
						:key="p.Id"
						class="bill-info__sub"
					>
						{{ i + 1 }}) {{ $t(p.Type === BillType.Incoming ? 'inc_abbr' : 'out_abbr') }}<template v-if="p.Counterparty?.Name">, {{ p.Counterparty.Name }}</template>,
						{{ formatShortDate(p.Date) }}, {{ formatMoney(p.TotalAmount) }} ({{ p.PaymentPercent ?? 0 }}%)
					</div>
				</div>

				<!-- Привязки (редактор) -->
				<div
					v-if="connRows.length && !isEditing"
					class="bill-info__block"
				>
					<div class="bill-info__label">{{ $t('linked') }}:</div>
					<template
						v-for="row in connRows"
						:key="row.Descriptor + '-' + row.Id"
					>
						<bill-connection-project
							v-if="row.Descriptor === BillConnectionWithValuesTypeDescriptor.Project"
							:row="row"
							:percent-error="percentErr"
							:amount-error="amountErr"
							:payed-error="debitErr"
							@input="(t, v) => onFieldInput(row, t, v)"
							@blur-field="onFieldBlur(row)"
							@detach="detachConnection(row)"
						/>
						<bill-connection-income
							v-else
							:row="row"
							:percent-error="percentErr"
							:amount-error="amountErr"
							:payed-error="debitErr"
							@input="(t, v) => onFieldInput(row, t, v)"
							@blur-field="onFieldBlur(row)"
							@detach="detachConnection(row)"
							@move-info="openMovement(row.income)"
						/>
					</template>

					<div
						v-if="connError"
						class="bill-info__error"
					>{{ connError }}</div>

					<div
						v-if="connDirty"
						class="bill-info__actions bill-info__actions--conn"
					>
						<button
							class="bill-info__btn bill-info__btn--primary"
							@click="saveConnections"
						>{{ $t('bill_save_links') }}</button>
					</div>
				</div>

				<div class="bill-info__line">
					<span class="bill-info__label">{{ $t('one_c_instance') }}:</span>
					<span class="bill-info__value">{{ info.Instance1C || $t('by_default') }}</span>
				</div>

				<!-- Комментарий -->
				<div
					v-if="isEditing || info.Note"
					class="bill-info__line bill-info__line--field"
				>
					<span class="bill-info__label">{{ $t('comment') }}:</span>
					<template v-if="!isEditing">
						<span class="bill-info__value">{{ info.Note }}</span>
						<app-tooltip :text="$t('edit_label')">
							<template #activator>
								<app-icon
									v-if="canEdit"
									class="bill-info__note-edit"
									name="edit-pencil"
									width="14px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
									@click="startEdit"
								/>
							</template>
						</app-tooltip>
					</template>
					<app-input
						v-else
						v-model="editForm.Note"
						class="bill-info__input"
						:placeholder="$t('comment')"
					/>
				</div>

				<!-- Файл (загрузка/замена) -->
				<div
					v-if="isEditing"
					class="bill-info__line bill-info__line--field"
				>
					<span class="bill-info__label">{{ $t('file_label') }}:</span>
					<span
						v-if="info.File"
						class="bill-info__value"
					>{{ info.File.Name }}</span>
					<input
						ref="$file"
						type="file"
						class="bill-info__file-hidden"
						@change="onFileSelected"
					>
					<button
						class="bill-info__btn"
						@click="$file?.click()"
					>{{ $t(info.File ? 'file_replace_file' : 'file_upload') }}</button>
				</div>

				<!-- Файл -->
				<div
					v-if="info.File && !isEditing"
					class="bill-info__line"
				>
					<span class="bill-info__label">{{ $t('file_label') }}:</span>
					<span
						class="bill-info__file"
						@click="downloadFile(info.File)"
					>
						<app-icon
							name="download"
							width="14px"
							color="#499259"
							cursor="pointer"
						/>
						{{ info.File.Name }}
					</span>
				</div>

				<!-- Действия по счёту -->
				<div
					v-if="canEdit"
					class="bill-info__actions"
				>
					<template v-if="isEditing">
						<button
							class="bill-info__btn bill-info__btn--primary"
							:disabled="sumInvalid"
							@click="saveEdit"
						>{{ $t('save') }}</button>
						<button
							class="bill-info__btn"
							@click="cancelEdit"
						>{{ $t('cancel_short') }}</button>
					</template>
					<button
						v-else
						class="bill-info__btn"
						@click="startEdit"
					>{{ $t('edit_label') }}</button>
				</div>
			</template>

			<div
				v-else-if="!isLoading && !info"
				class="bill-info__empty"
			>
				{{ $t('bill_load_error') }}
			</div>
		</div>

		<!-- каскад: инфо КА контрагента этого счёта (той же модалкой) -->
		<counterparty-info-modal ref="$cpInfoModal" />
	</base-modal>
</template>

<script setup lang="ts">
import {
	computed,
	defineAsyncComponent,
	reactive,
	ref
} from "vue";
import { i18n } from "@/i18n";

const t = (key: string, named?: Record<string, unknown>): string => String(named ? i18n.global.t(key, named) : i18n.global.t(key));
import { useQuasar } from "quasar";
import BaseModal from "@/components/common/modal/BaseModal.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import MoveInfoDialog from "@/components/multiedit/temporary/storageshared/MoveInfoDialog.vue";
import BillConnectionProject from "./bill-connections/BillConnectionProject.vue";
import BillConnectionIncome from "./bill-connections/BillConnectionIncome.vue";
import { FileApi } from "@/ api/FileApi";
import { financesStore, commonRequestCreds } from "@/store/finances";
import { useBillConnectionsEditor } from "@/composables/useBillConnectionsEditor";
import {
	BillInfo,
	BillEditAsker,
	StorageItemMovementInFinances
} from "@/models/interfaces/finances/finance-models";
import { CommonEntity } from "@/models/interfaces/common";
import { BillType, BillConnectionWithValuesTypeDescriptor } from "@/models/etc/types";
import { formatShortDate, payStatusText, barWidth, formatMoney } from "./bill-format";
import { useModalEscape } from "@/composables/useModalEscape";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

const CounterpartyInfoModal = defineAsyncComponent(() => import("./CounterpartyInfoModal.vue"))

const emit = defineEmits<{
	(e: 'changed'): void,
}>()

const info = ref<BillInfo | null>(null)
const isLoading = ref(true)
const isSaving = ref(false)
const currentId = ref(0)

const isEditing = ref(false)
const editForm = reactive({ Number: '', Sum: '', Note: '' })

const $content = ref<HTMLElement | null>(null)
const $modal = ref<InstanceType<typeof BaseModal> | null>(null)
const $file = ref<HTMLInputElement | null>(null)
const $cpInfoModal = ref<{ onOpen: (id: number) => void } | null>(null)
const $q = useQuasar()

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose(),
	// В режиме правки первый Escape отменяет редактирование, второй закрывает модалку.
	intercept: () => {
		if (!isEditing.value) {
			return false
		}
		cancelEdit()
		return true
	}
})

// редактор привязок
const {
	rows: connRows,
	dirty: connDirty,
	errorText: connError,
	percentErr,
	amountErr,
	debitErr,
	init: initConnections,
	onFieldInput,
	onFieldBlur,
	detach: detachConnection,
	save: saveConnectionsRaw
} = useBillConnectionsEditor(info)

const modalTitle = computed(() => {
	const num = info.value?.Number || ''
	const base = num ? t('bill_info_title_numbered', { number: num }) : t('bill_info_title')
	return info.value?.IsDraft ? t('draft_suffix', { base }) : base
})

const statusText = computed(() => {
	const m = info.value
	if (!m) return ''
	return t((m.IsDraft || m.CreatedInCrm) ? 'bill_status_draft' : 'bill_status_approved')
})

const typeText = computed(() =>
	t(info.value?.Type === BillType.Incoming ? 'bill_type_incoming' : 'bill_type_outgoing')
)

const isOutgoing = computed(() => info.value?.Type === BillType.Outgoing)

// редактирование доступно только для черновиков / созданных в CRM
const canEdit = computed(() => !!info.value && (info.value.IsDraft || info.value.CreatedInCrm))

const parsedSum = computed(() => Number(String(editForm.Sum).replace(',', '.')))
const sumInvalid = computed(() =>
	String(editForm.Sum).trim() === '' || Number.isNaN(parsedSum.value) || parsedSum.value < 0
)

const onOpen = async (billId: number) => {
	info.value = null
	currentId.value = billId
	isEditing.value = false
	isLoading.value = true
	$modal.value?.onOpen()
	activateEsc()
	try {
		const data = await financesStore().getInfoCBill(billId)

		info.value = data && data.Id ? data : null
		initConnections()
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const startEdit = () => {
	const m = info.value
	if (!m) {
		return
	}
	editForm.Number = m.Number ?? ''
	editForm.Sum = String(m.Amount ?? '')
	editForm.Note = m.Note ?? ''
	isEditing.value = true
}

const cancelEdit = () => {
	isEditing.value = false
}

const reloadInfo = async () => {
	const data = await financesStore().getInfoCBill(currentId.value)
	if (data && data.Id) {
		info.value = data
	}
	initConnections()
}

const saveEdit = async () => {
	const m = info.value
	if (!m || sumInvalid.value) {
		return
	}
	const asker: BillEditAsker = {
		...commonRequestCreds(),
		Id: currentId.value,
		Sum: parsedSum.value,
		Note: editForm.Note,
		Number: isOutgoing.value ? (m.Number ?? '') : editForm.Number,
		FileId: m.File?.Id ?? 0,
		Rate: m.Rate ?? 0,
	}
	try {
		isSaving.value = true
		const res = await financesStore().billEdit(asker)
		if (res.response_ok) {
			await reloadInfo()
			isEditing.value = false
			emit('changed')
		}
	} catch (e) {
		console.error(e)
	} finally {
		isSaving.value = false
	}
}

const saveConnections = async () => {
	try {
		isSaving.value = true
		const ok = await saveConnectionsRaw(currentId.value)
		if (ok) {
			await reloadInfo()
			emit('changed')
		}
	} catch (e) {
		console.error(e)
	} finally {
		isSaving.value = false
	}
}

const onModalClose = () => $modal.value?.onClose()

const downloadFile = (file: CommonEntity | null | undefined) => {
	if (file?.Id) FileApi.FileDownload(file.Id)
}

// Загрузка/замена файла счёта. Форма FormData повторяет Angular:
// поле 'data' = blob, а имя файла в append несёт JSON-метаданные (IFormFile).
const onFileSelected = async (e: Event) => {
	const input = e.target as HTMLInputElement
	const file = input.files?.[0]
	if (!file) {
		return
	}
	const meta = { ...commonRequestCreds(), BillId: currentId.value, Filename: file.name }
	const formData = new FormData()
	formData.append('data', file, JSON.stringify(meta))
	try {
		isSaving.value = true
		await financesStore().AddFileToBill(formData)
		await reloadInfo()
		emit('changed')
	} catch (err) {
		console.error(err)
	} finally {
		isSaving.value = false
		input.value = ''
	}
}

const openMovement = (income: StorageItemMovementInFinances | undefined) => {
	if (!income?.Id) {
		return
	}
	$q.dialog({
		component: MoveInfoDialog,
		componentProps: { existingMovementId: income.Id }
	})
}

const openCounterparty = () => {
	const id = info.value?.Counterparty?.Id
	if (id != null) {
		$cpInfoModal.value?.onOpen(Number(id))
	}
}

defineExpose({ onOpen, onClose: onModalClose })
</script>

<style scoped lang="scss">
.bill-info {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 12px;

	&__line {
		font-size: 14px;
		line-height: 18px;

		&--field {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			gap: 6px;
		}
	}

	&__label {
		font-weight: 500;
		color: #4D4D4D;
	}

	&__value {
		margin-left: 6px;
		color: #333;
		word-break: break-word;
	}

	&__line--field &__value {
		margin-left: 0;
	}

	&__field {
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}

	&__input {
		width: 220px;
	}

	&__error {
		font-size: 12px;
		line-height: 16px;
		color: #FF5533;
	}

	&__green {
		color: #499259;
	}

	&__cp {
		margin-left: 6px;
		color: #438451;
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}

	&__block {
		display: flex;
		flex-direction: column;
		gap: 6px;
		margin-top: 4px;

		.bill-info__label {
			font-weight: 500;
		}
	}

	&__sub {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		font-size: 13px;
		line-height: 18px;
		color: #4D4D4D;
	}

	&__bar {
		display: inline-block;
		vertical-align: middle;
		width: 64px;
		height: 6px;
		margin: 0 4px;
		border-radius: 3px;
		background: #EDEDED;
		overflow: hidden;

		&-fill {
			display: block;
			height: 100%;
			border-radius: 3px;
			background: #499259;
			transition: width 0.2s ease;
		}
	}

	&__note-edit {
		margin-top: -2px;
	}

	&__file {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		margin-left: 6px;
		color: #438451;
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}

	&__file-hidden {
		display: none;
	}

	&__actions {
		display: flex;
		gap: 8px;
		margin-top: 12px;

		&--conn {
			margin-top: 4px;
		}
	}

	&__btn {
		height: 32px;
		padding: 0 16px;
		border: 1px solid #D2D2D2;
		border-radius: 8px;
		background: #fff;
		color: #4D4D4D;
		font-size: 13px;
		line-height: 18px;
		cursor: pointer;
		transition: border-color 0.1s, color 0.1s;

		&:hover {
			border-color: #499259;
			color: #438451;
		}

		&--primary {
			border-color: #499259;
			background: #499259;
			color: #fff;

			&:hover {
				background: #438451;
				border-color: #438451;
				color: #fff;
			}
		}

		&:disabled {
			opacity: 0.5;
			cursor: default;
			border-color: #D2D2D2;
			background: #fff;
			color: #979797;
		}

		&--primary:disabled {
			background: #fff;
		}
	}

	&__empty {
		font-size: 14px;
		line-height: 18px;
		color: #979797;
		padding: 12px 0;
	}
}
</style>
